import _ from "lodash";
import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import type {
  DataMapping,
  MappingData,
  TargetMappingData,
} from "../../../api/src/lib/dataMapping";
import Loading from "../Loading";
import Content from "./Content";
import SubmitButton from "./SubmitButton";
import { capitalize } from "../lib/display";

// One entry of the menu of an item: its text, and its row (none for the
// unassigned list).
type ItemMove = { label: string; targetId?: number };

// The toggle of the menu of an item. It is the pill itself and ignores the
// click and key props of `Dropdown.Toggle`: a pointer drags the pill or
// double-clicks it, and only Enter or Space opens the menu.
const PillToggle = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { onOpen: () => void }
>(({ onOpen, children, className, onClick, onKeyDown, ...rest }, ref) => {
  void onClick;
  void onKeyDown;
  return (
    <div
      {...rest}
      ref={ref}
      className={className}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onOpen();
      }}
    >
      {children}
    </div>
  );
});
PillToggle.displayName = "PillToggle";

const DraggableText = ({
  previousTargetId,
  sourceId,
  name,
  handleReleaseLink,
  moves,
  onMove,
}: {
  previousTargetId?: number;
  sourceId: number | string;
  name: string;
  handleReleaseLink?: (sourceId: number | string) => void;
  moves: ItemMove[];
  onMove: (
    transfered: { previousTargetId?: number; sourceId: number | string },
    targetId?: number,
  ) => void;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const handleDoubleClick = useCallback(() => {
    if (
      typeof handleReleaseLink !== "undefined" &&
      typeof sourceId !== "undefined"
    )
      handleReleaseLink(sourceId);
  }, [sourceId, handleReleaseLink]);

  const handleDragStart = (event: React.DragEvent<HTMLElement>) => {
    event.dataTransfer.setData(
      "text",
      JSON.stringify({
        previousTargetId,
        sourceId,
      }),
    );
  };

  // Enter or Space on the pill opens the menu of its moves; an entry makes the
  // move of the drop, and Escape closes the menu with no change.
  return (
    <Dropdown
      show={menuOpen}
      onToggle={(nextShow) => {
        if (!nextShow) setMenuOpen(false);
      }}
      focusFirstItemOnShow
    >
      <Dropdown.Toggle
        as={PillToggle}
        onOpen={() => setMenuOpen(true)}
        className="draggable-item border border-secondary rounded-pill px-3 py-1 user-select-none"
        draggable
        onDragStart={handleDragStart}
        role="button"
        tabIndex={0}
        onDoubleClick={handleDoubleClick}
      >
        {name}
      </Dropdown.Toggle>
      <Dropdown.Menu
        aria-label={`Move ${name}`}
        popperConfig={{ strategy: "fixed" }}
      >
        {moves.length > 0 ? (
          moves.map((move) => (
            <Dropdown.Item
              key={move.targetId ?? "unassigned"}
              as="button"
              onClick={() =>
                onMove({ previousTargetId, sourceId }, move.targetId)
              }
            >
              {move.label}
            </Dropdown.Item>
          ))
        ) : (
          <Dropdown.Item as="button" disabled>
            No row can take this item
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const DropBox = ({
  className = "",
  children,
  targetId,
  handleDropItem,
}: {
  className?: string;
  children: React.ReactNode;
  targetId?: number;
  handleDropItem: (
    transfered: {
      previousTargetId?: number;
      sourceId: number | string;
    },
    targetId?: number,
  ) => void;
}) => {
  const [dragOver, setDragOver] = useState(false);
  const handleDragOverStart = () => setDragOver(true);
  const handleDragOverEnd = () => setDragOver(false);

  const enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const transferedData = event.dataTransfer.getData("text");
    handleDropItem(JSON.parse(transferedData), targetId);
    setDragOver(false);
  };

  return (
    // Keep the drag target area large, but wrap the items narrowly
    <div
      className={`${className} w-100 user-select-none ${
        dragOver ? "mapping-box-drag-over" : ""
      }`}
      onDragOver={enableDropping}
      onDrop={handleDrop}
      onDragEnter={handleDragOverStart}
      onDragLeave={handleDragOverEnd}
    >
      <div className="d-flex flex-wrap align-items-start gap-2">{children}</div>
    </div>
  );
};

// The state of an action of the advisor whose answer sets the rows: `started`
// from the click until the caller reports it running, then `running` until the
// caller reports it done (R37, R38).
type ActionState = "idle" | "started" | "running";

const stepAction = (action: ActionState, isRunning: boolean): ActionState => {
  if (action === "started" && isRunning) return "running";
  if (action === "running" && !isRunning) return "idle";
  return action;
};

// The rows of a read for one CRM: each row with its own items and its held
// suggestions.
const rowsFromRead = <TData,>(
  data: DataMapping<TData, Pick<MappingData, "id" | "name">>,
  service: string,
): TargetMappingData<TData>[] => {
  const names = _.keyBy(data.source[service] ?? [], "id");
  return data.target.map((target) => ({
    ...target,
    assigned: {
      ...target.assigned,
      [service]: [
        ...(target.assigned[service] ?? []),
        ...(typeof data.suggested[target.id] !== "undefined"
          ? data.suggested[target.id].map((id) => ({
              id,
              name: names[id]?.name ?? "",
            }))
          : []),
      ],
    },
  }));
};

const assignedIds = (
  rows: { assigned?: { [key: string]: { id: number | string }[] } }[],
  service: string,
) =>
  JSON.stringify(
    rows.map((row) => (row.assigned?.[service] ?? []).map((i) => i.id).sort()),
  );

// Whether the rows differ from the rows of the read they last saw, with or
// without its suggested items (a "Revert" drops them), or a "New" row holds an
// item.
const hasUnsavedMove = <TData,>(
  rows: TargetMappingData<TData>[],
  newRows: { assigned?: { [key: string]: unknown[] } }[],
  seen: DataMapping<TData, Pick<MappingData, "id" | "name">>,
  service: string,
) => {
  if (newRows.some((row) => (row.assigned?.[service] ?? []).length > 0))
    return true;
  const shown = assignedIds(rows, service);
  return (
    shown !== assignedIds(rowsFromRead(seen, service), service) &&
    shown !== assignedIds(seen.target, service)
  );
};

// The default for `onSetService`. It is one function for every render, so the
// effect that reports `selectedService` does not run again on each render of a
// caller that passes no `onSetService`.
const ignoreService = () => null;

function DataMappingModal<TData>({
  linkHeader,
  title,
  targetTitle,
  mappedTitle,
  sourceTitle,
  providerOptions = [],
  initialService,
  isLoading,
  isError,
  mappingData,
  handleSubmit,
  isSubmitting,
  onAutoSuggest = () => null,
  onSetService = ignoreService,
  isLoadingSuggestions = false,
  isChangingRows = false,
  oneToMany = true,
  additionalButtons,
  showNewRows = false,
  selectedTags,
  onTagFilterChange,
  availableTags,
  enforceSingleProvider = false,
}: {
  linkHeader?: string;
  title: string;
  targetTitle: string;
  sourceTitle: string;
  mappedTitle?: string;
  providerOptions?: string[];
  // The CRM selected on the first render (APP-1692). Without it, the first
  // entry of `providerOptions` is selected, as before.
  initialService?: string;
  isLoading: boolean;
  isError: boolean;
  // An entry of `source` is a CRM record, which has `tags`, or an account
  // (`AccountsHouseholdsMapping`), which does not.
  mappingData: DataMapping<TData, Pick<MappingData, "id" | "name">>;
  handleSubmit: (
    data: TargetMappingData<TData>[],
    unlinkProviders?: string[],
  ) => Promise<void>;
  isSubmitting: boolean;
  onAutoSuggest?: () => void;
  onSetService?: (service: string) => void;
  isLoadingSuggestions?: boolean;
  // An action of the caller that writes new rows into the read, such as
  // "Create from Unlinked", runs; a read then sets the rows (R55).
  isChangingRows?: boolean;
  oneToMany?: boolean;
  additionalButtons?: ReactNode;
  showNewRows?: boolean;
  selectedTags?: string[];
  onTagFilterChange?: (tags: string[]) => void;
  availableTags?: string[];
  enforceSingleProvider?: boolean;
}) {
  const [targetData, setTargetData] = useState<TargetMappingData<TData>[]>([]);
  const [selectedService, setSelectedService] = useState<string>(
    initialService ?? providerOptions[0] ?? "",
  );

  useEffect(() => {
    if (selectedService === "") {
      const updatedService =
        providerOptions.length > 0
          ? providerOptions[0]
          : (Object.keys(mappingData.source)[0] ?? "");

      setSelectedService(updatedService);
    }
  }, [mappingData.source, providerOptions, selectedService]);

  // Report `selectedService` to the parent each time it changes, the starting
  // value included (APP-1685). The parent builds request URLs from it; when
  // `providerOptions` was not empty on the first render, the parent was not
  // told the starting value and "Auto-Match" sent no CRM name.
  useEffect(() => {
    onSetService(selectedService);
  }, [onSetService, selectedService]);

  const sourceDataMap = useMemo(
    () => _.keyBy(mappingData.source[selectedService] ?? [], "id"),
    [mappingData.source, selectedService],
  );
  const targetDataMap = useMemo(() => _.keyBy(targetData, "id"), [targetData]);

  // New Row data structure
  type AssignedItem = {
    id: number | string;
    name: string;
  };

  type DataRow = {
    id: number;
    name: string;
    assigned?: { [key: string]: AssignedItem[] };
  };

  const [newRows, setNewRows] = useState<DataRow[]>([{ id: -1, name: "New" }]);

  // The rows are set from the read when the CRM changes (R40), when the read
  // first returns, while a "Save", an "Auto-Match" or another action of the
  // advisor is open (R37, R38), and when the rows hold no unsaved move. A read
  // that the advisor did not start keeps each unsaved move (R55, as Go R86).
  const seenRef = useRef<{
    data: typeof mappingData;
    service: string;
  } | null>(null);
  const actionsRef = useRef<{ save: ActionState; suggest: ActionState }>({
    save: "idle",
    suggest: "idle",
  });

  useEffect(() => {
    if (selectedService === "") return;
    const seen = seenRef.current;
    const actions = actionsRef.current;
    const changed = seen !== null && seen.data !== mappingData;
    let reset = seen === null || seen.service !== selectedService;
    if (!reset && changed) {
      const open =
        isChangingRows || actions.save !== "idle" || actions.suggest !== "idle";
      reset =
        open ||
        !hasUnsavedMove(targetData, newRows, seen.data, selectedService);
    }
    if (reset) {
      setTargetData(rowsFromRead(mappingData, selectedService));
      seenRef.current = { data: mappingData, service: selectedService };
      if (actions.save !== "running") actions.save = "idle";
      if (actions.suggest !== "running") actions.suggest = "idle";
    } else if (changed) {
      seenRef.current = { data: mappingData, service: selectedService };
    }
    actions.save = stepAction(actions.save, isSubmitting);
    actions.suggest = stepAction(actions.suggest, isLoadingSuggestions);
  }, [
    mappingData,
    selectedService,
    isSubmitting,
    isLoadingSuggestions,
    isChangingRows,
    targetData,
    newRows,
  ]);

  // Get unlinked data
  const getUnlinkedData = (selectedService: string) => {
    return (mappingData.source[selectedService] ?? []).filter(
      (data) =>
        !targetData.some((target) =>
          (target.assigned[selectedService] ?? []).some(
            (assignedItem) => assignedItem.id === data.id,
          ),
        ) && // not assigned to any target
        !newRows.some((row) =>
          (row.assigned?.[selectedService] ?? []).some(
            (assignedItem) => assignedItem.id === data.id,
          ),
        ), // not assigned to any new Rows
    );
  };
  const allUnlinkedData = getUnlinkedData(selectedService);
  const unlinkedData =
    (selectedTags ?? []).length > 0
      ? allUnlinkedData.filter((item) =>
          ((item as MappingData).tags ?? []).some((t) =>
            selectedTags!.includes(typeof t === "string" ? t : t.name),
          ),
        )
      : allUnlinkedData;

  // Check if there are unlinked data or assigned rows in newRows
  const shouldShowNewRows =
    showNewRows &&
    (unlinkedData.length > 0 ||
      (unlinkedData.length === 0 && newRows.some((row) => "assigned" in row)));

  // The handleAddRow function is used to add a new row to the newRows state.
  // If there are no previous rows, the id is set to -1.
  // The new object has an id which is one less than the id of the last row in the previous rows.
  const handleAddRow = () => {
    setNewRows((prevRows) => [
      ...prevRows,
      {
        id: prevRows.length ? prevRows[prevRows.length - 1].id - 1 : -1,
        name: "",
      },
    ]);
  };

  const handleDropItem = useCallback(
    (
      draggedItem: {
        previousTargetId?: number;
        sourceId: number | string;
      },
      targetId?: number,
    ) => {
      type AssignedType = {
        [key: string]: { id: number | string; name: string }[];
      };

      // Function to update the assigned items of a target
      const updateAssignedItems = (
        assigned: AssignedType | undefined,
        shouldRemove: boolean,
      ) => {
        const assignedItems = assigned?.[selectedService] ?? [];
        // Determine the updated items based on whether the item should be removed or added
        const updatedItems = shouldRemove
          ? assignedItems.filter((item) => item.id !== draggedItem.sourceId)
          : oneToMany // Check if oneToMany is true
            ? [
                ...assignedItems,
                {
                  id: draggedItem.sourceId,
                  name: sourceDataMap[draggedItem.sourceId]?.name ?? "",
                }, // Add the dragged item
              ]
            : assignedItems.length > 0 // Check if there is already an item in the target
              ? assignedItems // If there is already an item in the target, return the existing assigned items
              : [
                  {
                    id: draggedItem.sourceId,
                    name: sourceDataMap[draggedItem.sourceId]?.name ?? "",
                  }, // Add the dragged item
                ];

        return { ...assigned, [selectedService]: updatedItems };
      };

      // Function to update the new rows when new row item is dragged in or out
      const updateNewRows = (
        targetId: number | undefined,
        draggedItem: { previousTargetId?: number; sourceId: number | string },
      ) => {
        setNewRows((prevRows) =>
          prevRows.map((row) => {
            const shouldRemove =
              // If the item was not dropped on a target and not dragged from a target
              (typeof targetId === "undefined" &&
                typeof draggedItem.previousTargetId === "undefined") ||
              // If the item was dropped on a target but not dragged from a target
              (typeof targetId !== "undefined" &&
                targetId > 0 &&
                typeof draggedItem.previousTargetId === "undefined") ||
              // If the item was already assigned to the selected service
              (row.assigned?.[selectedService]?.some(
                (item) => item.id === draggedItem.sourceId,
              ) ??
                false);

            // Determine if the row should be updated based on whether the item was dropped on this row or should be removed
            const shouldUpdate = row.id === targetId || shouldRemove;

            // If the item was dropped on this row or removed from it, update the assigned items
            return shouldUpdate
              ? {
                  ...row,
                  assigned: updateAssignedItems(row.assigned, shouldRemove),
                }
              : row; // Otherwise, return the row as is
          }),
        );
      };

      // Function to update the target data
      const updateTargetData = (
        targetId: number | undefined,
        draggedItem: { previousTargetId?: number; sourceId: number | string },
      ) => {
        setTargetData((targetData) =>
          targetData.map((target) => {
            // Check if the item was dragged from this target
            const shouldRemove = target.id === draggedItem.previousTargetId;
            // Check if the item was dropped on this target or removed from it
            const shouldUpdate = target.id === targetId || shouldRemove;

            // If the item was dropped on this target or removed from it, update the assigned items
            return shouldUpdate
              ? {
                  ...target,
                  assigned: updateAssignedItems(target.assigned, shouldRemove),
                }
              : target; // Otherwise, return the target as is
          }),
        );
      };

      // Determine the target where the item was dropped
      // If targetId is undefined or less than 0, set foundTarget to null
      // Otherwise, find the target in targetDataMap using targetId
      const foundTarget =
        typeof targetId === "undefined" || targetId < 0
          ? null
          : targetDataMap[targetId];

      // If the item was not dropped on a target (targetId is undefined)
      // and the item was not dragged from a target (draggedItem.previousTargetId is undefined)
      // then update the new rows
      if (
        typeof targetId === "undefined" &&
        typeof draggedItem.previousTargetId === "undefined"
      ) {
        updateNewRows(targetId, draggedItem);
      } else if (
        // If the target is empty (foundTarget is null or has no assigned items)
        // or the target allows multiple items (oneToMany is true)
        // and the item was not dragged from this target (targetId is not equal to draggedItem.previousTargetId)

        (foundTarget === null ||
          (foundTarget.assigned[selectedService] ?? []).length <= 0 ||
          oneToMany) &&
        targetId !== draggedItem.previousTargetId
      ) {
        // Check if the target is a new row (targetId is not null and less than 0)
        if (targetId != null && targetId < 0) {
          // If the target is a new row, update the new rows with the dragged item
          updateNewRows(targetId, draggedItem);

          // If the dragged item was previously assigned to a target, update the target data
          if (draggedItem.previousTargetId) {
            updateTargetData(draggedItem.previousTargetId, draggedItem);
          }
        } else {
          // If the target is not a new row, update the target data

          // If the dragged item was not previously assigned to a target, update the new rows
          if (typeof draggedItem.previousTargetId === "undefined") {
            updateNewRows(targetId, draggedItem);
          }

          // Update the target data with the dragged item
          updateTargetData(targetId, draggedItem);
        }
      }
    },
    [targetDataMap, selectedService, oneToMany, sourceDataMap],
  );

  // One CRM link for each household (APP-1295, APP-1684): when
  // enforceSingleProvider is set, a drop onto a household that has a link to a
  // different CRM is applied at once, with no dialog. The link to the other CRM
  // stays in `targetData` until Save. This map gives, for each household, the
  // CRMs whose link Save removes: the household got a link to a CRM in this
  // session (no link in the loaded `mappingData.target`, a link now) and still
  // has a link to a different CRM that the loaded data has. If the advisor
  // removes the new link before Save, the household leaves the map and its
  // earlier link stays.
  const unlinkOnSave = useMemo(() => {
    const result: Record<string, string[]> = {};
    if (!enforceSingleProvider) return result;
    const loadedTargetMap = _.keyBy(mappingData.target, "id");
    const hasLink = (assigned: Record<string, unknown[]>, provider: string) =>
      (assigned[provider] ?? []).length > 0;
    for (const target of targetData) {
      const loaded = loadedTargetMap[target.id];
      if (Number(target.id) < 0 || typeof loaded === "undefined") continue;
      const providers = Object.keys(target.assigned);
      const gotLink = providers.some(
        (provider) =>
          hasLink(target.assigned, provider) &&
          !hasLink(loaded.assigned, provider),
      );
      if (!gotLink) continue;
      const earlierProviders = providers.filter(
        (provider) =>
          hasLink(target.assigned, provider) &&
          hasLink(loaded.assigned, provider),
      );
      if (earlierProviders.length > 0) result[target.id] = earlierProviders;
    }
    return result;
  }, [enforceSingleProvider, mappingData.target, targetData]);

  const handleReleaseLink = useCallback(
    (sourceId: number | string) => {
      setTargetData((targetData) =>
        targetData.map((target) => ({
          ...target,
          assigned: {
            ...target.assigned,
            [selectedService]: (target.assigned[selectedService] ?? []).filter(
              (assignedItem) => assignedItem.id !== sourceId,
            ),
          },
        })),
      );
      setNewRows((newRows) =>
        newRows.map((row) => ({
          ...row,
          assigned: {
            ...row.assigned,
            [selectedService]: (row.assigned?.[selectedService] ?? []).filter(
              (assignedItem) => assignedItem.id !== sourceId,
            ),
          },
        })),
      );
    },
    [selectedService],
  );

  const handleAutoSuggest = useCallback(() => {
    actionsRef.current.suggest = "started";
    onAutoSuggest();
  }, [onAutoSuggest]);

  const handleResetClick = useCallback(() => {
    setTargetData(mappingData.target);
    setNewRows([{ id: -1, name: "New" }]);
  }, [mappingData.target, setTargetData]);

  const handleKeepChange = useCallback(async () => {
    // Map over the newRows array and ensure each row has an 'assigned' property
    // Cast each row to the TargetMappingData<TData> type
    const mappedNewRows = newRows.map(
      (row) =>
        ({ ...row, assigned: row.assigned ?? {} }) as TargetMappingData<TData>,
    );

    // Remove the earlier link of each household in `unlinkOnSave` from the
    // submitted data. `unlinkProviders` names each CRM that loses a link. This
    // is load-bearing: the submit drops a CRM that has no household left, and
    // the backend runs the unlinks of a CRM only when its key is in the payload.
    const submittedTargetData = targetData.map((target) => {
      const earlierProviders = unlinkOnSave[target.id] ?? [];
      return earlierProviders.length <= 0
        ? target
        : {
            ...target,
            assigned: {
              ...target.assigned,
              ...Object.fromEntries(
                earlierProviders.map((provider) => [provider, []]),
              ),
            },
          };
    });
    const unlinkProviders = _.uniq(Object.values(unlinkOnSave).flat());

    // If showNewRows is true, combine the target data and mappedNewRows arrays
    // Otherwise, use the target data array as is
    const combinedData = showNewRows
      ? [...submittedTargetData, ...mappedNewRows]
      : submittedTargetData;

    actionsRef.current.save = "started";
    await handleSubmit(combinedData, unlinkProviders);

    // Reset the newRows state to its initial value
    setNewRows([{ id: -1, name: "New" }]);
  }, [showNewRows, targetData, newRows, handleSubmit, unlinkOnSave]);

  const [unChanged, setUnChanged] = useState(false);
  useEffect(() => {
    // Create a new array that includes all items from targetData and, if any newRows have an 'assigned' property, all newRows
    const combinedData = [
      ...targetData,
      ...(newRows.some((row) => "assigned" in row)
        ? newRows.map((row) => ({ ...row, assigned: row.assigned ?? {} }))
        : []),
    ];

    // For each row in the combined data, get the IDs of the assigned items for the selected service and sort them
    const sortedAssignedIds = combinedData.map((row) =>
      (row.assigned[selectedService] ?? [])
        .map((assignedItem) => assignedItem.id)
        .sort(),
    );

    // Do the same for the original mapping data
    const originalSortedAssignedIds = mappingData.target.map((target) =>
      (target.assigned[selectedService] ?? [])
        .map((assignedItem) => assignedItem.id)
        .sort(),
    );

    // Compare the two arrays. If they are the same, setUnChanged will be set to true. If they are different, setUnChanged will be set to false.
    setUnChanged(
      JSON.stringify(sortedAssignedIds) ===
        JSON.stringify(originalSortedAssignedIds),
    );
  }, [targetData, mappingData, setUnChanged, selectedService, newRows]); // Dependencies for the useEffect hook

  const [searchValue, setSearchValue] = useState("");
  const onSetSearchValue = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(ev.currentTarget.value);
    },
    [],
  );

  // The menu of an item: each shown row that can take it, as a drop can, then,
  // for an item of a row, the unassigned list.
  const searchUpper = searchValue.toUpperCase();
  const searchedTargets = targetData.filter(
    (target) =>
      target.name.toUpperCase().includes(searchUpper) ||
      (target.assigned[selectedService] ?? []).some((assigned) =>
        (assigned.name ?? "").toUpperCase().includes(searchUpper),
      ),
  );
  const movesOf = (previousTargetId?: number): ItemMove[] => [
    ...[
      ...(shouldShowNewRows
        ? newRows.map((row) => ({
            id: row.id,
            name: "New",
            count: (row.assigned?.[selectedService] ?? []).length,
          }))
        : []),
      ...searchedTargets.map((target) => ({
        id: Number(target.id),
        name: target.name,
        count: (target.assigned[selectedService] ?? []).length,
      })),
    ]
      .filter(
        (row) => row.id !== previousTargetId && (oneToMany || row.count <= 0),
      )
      .map((row) => ({ label: `Move to ${row.name}`, targetId: row.id })),
    ...(typeof previousTargetId === "undefined"
      ? []
      : [{ label: `Move to ${sourceTitle}` }]),
  ];

  return (
    <>
      <h1>{title}</h1>
      <Content>
        <Row>
          <Col md={8}>
            <Row>
              <Col className="d-flex align-items-end">
                <h3>{targetTitle}</h3>
              </Col>
              <Col xs="auto">
                {providerOptions.length > 0 ? (
                  <Form.Group
                    controlId="form-provider"
                    className="mb-2"
                    style={{ maxWidth: "12rem" }}
                  >
                    <Form.Label>CRM</Form.Label>
                    <Form.Select
                      value={selectedService}
                      onChange={(ev) => {
                        setSelectedService(ev.currentTarget.value);
                      }}
                    >
                      {providerOptions.map((providerOption) => (
                        <option key={providerOption} value={providerOption}>
                          {capitalize(providerOption)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                ) : typeof mappedTitle === "undefined" ? null : (
                  <h3>{mappedTitle}</h3>
                )}
              </Col>
            </Row>
            <Row>
              <Col xl={6}>
                <InputGroup size="sm" className="mb-2">
                  <InputGroup.Text>Search</InputGroup.Text>
                  <Form.Control type="text" onChange={onSetSearchValue} />
                </InputGroup>
              </Col>
            </Row>
            {isLoading ? (
              <Loading />
            ) : isError ? (
              <Alert variant="danger">Error occurred</Alert>
            ) : targetData.length <= 0 && !shouldShowNewRows ? (
              <Alert>No data</Alert>
            ) : (
              <Row>
                <Col>
                  <div className="mapping-table-box border border-secondary rounded mb-3">
                    <Table className="mb-0">
                      <tbody>
                        {shouldShowNewRows
                          ? newRows.map((row, index) => {
                              // Check if the current row is the last one
                              const isLastRow = newRows.length - 1 === index;
                              // Check if there are multiple rows
                              const hasMultipleRows = newRows.length > 1;

                              return (
                                <tr key={row.id}>
                                  <td className="w-50">New</td>
                                  <td className="w-50">
                                    <DropBox
                                      handleDropItem={handleDropItem}
                                      targetId={row.id}
                                    >
                                      {(row.assigned?.[selectedService] ?? [])
                                        .length === 0 ? (
                                        <div className="text-secondary ps-3">
                                          Empty
                                        </div>
                                      ) : (
                                        row.assigned?.[selectedService].map(
                                          (item, index) => (
                                            <DraggableText
                                              key={item.id}
                                              sourceId={item.id}
                                              name={item.name}
                                              handleReleaseLink={
                                                handleReleaseLink
                                              }
                                              moves={movesOf(row.id)}
                                              onMove={handleDropItem}
                                            />
                                          ),
                                        )
                                      )}
                                    </DropBox>
                                  </td>
                                  <td>
                                    <ButtonGroup>
                                      {isLastRow && (
                                        <Button onClick={handleAddRow}>
                                          +
                                        </Button>
                                      )}
                                      {hasMultipleRows && (
                                        <Button
                                          onClick={() =>
                                            setNewRows((prevRows) =>
                                              prevRows.filter(
                                                (r) => r.id !== row.id,
                                              ),
                                            )
                                          }
                                        >
                                          -
                                        </Button>
                                      )}
                                    </ButtonGroup>
                                  </td>
                                </tr>
                              );
                            })
                          : null}
                        {targetData
                          .filter(
                            (target) =>
                              target.name
                                .toUpperCase()
                                .includes(searchValue.toUpperCase()) ||
                              (target.assigned[selectedService] ?? []).some(
                                (assigned) =>
                                  (assigned.name ?? "")
                                    .toUpperCase()
                                    .includes(searchValue.toUpperCase()),
                              ),
                          )
                          .map((target) => (
                            <tr key={target.id}>
                              <td className="w-50">
                                {typeof linkHeader !== "undefined" ? (
                                  <Link
                                    to={`${linkHeader}${target.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {target.name}
                                  </Link>
                                ) : (
                                  target.name
                                )}
                              </td>
                              <td className="w-50">
                                <DropBox
                                  handleDropItem={handleDropItem}
                                  targetId={Number(target.id)}
                                >
                                  {(target.assigned[selectedService] ?? [])
                                    .length === 0 ? (
                                    <div className="text-secondary ps-3">
                                      Empty
                                    </div>
                                  ) : (
                                    (
                                      target.assigned[selectedService] ?? []
                                    ).map((assignedItem) => (
                                      <DraggableText
                                        key={assignedItem.id}
                                        previousTargetId={Number(target.id)}
                                        sourceId={assignedItem.id}
                                        name={
                                          sourceDataMap[assignedItem.id]
                                            ?.name ?? ""
                                        }
                                        handleReleaseLink={handleReleaseLink}
                                        moves={movesOf(Number(target.id))}
                                        onMove={handleDropItem}
                                      />
                                    ))
                                  )}
                                </DropBox>
                                {(unlinkOnSave[target.id] ?? []).length > 0 ? (
                                  <div className="text-secondary small ps-3 pt-1">
                                    {unlinkOnSave[target.id]
                                      .map((provider) => capitalize(provider))
                                      .join(" and ")}{" "}
                                    link will be removed on Save
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                </Col>
              </Row>
            )}
          </Col>
          <Col md={4}>
            <h3>{sourceTitle}</h3>
            {(availableTags ?? []).length > 0 && (
              <Form.Group className="mb-2">
                <Form.Label>Filter by Tag</Form.Label>
                <Form.Select
                  value={selectedTags?.[0] ?? ""}
                  onChange={(e) => {
                    onTagFilterChange?.(e.target.value ? [e.target.value] : []);
                  }}
                >
                  <option value="">All Tags</option>
                  {availableTags!.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            {isLoading ? (
              <Loading />
            ) : isError ? (
              <Alert variant="danger">Error occurred</Alert>
            ) : selectedService === "" ||
              (mappingData.source[selectedService]?.length ?? 0) <= 0 ? (
              <Alert>No {sourceTitle} data</Alert>
            ) : (
              <div className="mapping-table-box border rounded p-3 d-flex align-items-stretch">
                <DropBox handleDropItem={handleDropItem}>
                  {unlinkedData.length === 0 ? (
                    <div className="text-secondary ps-3">
                      No {sourceTitle} data
                    </div>
                  ) : (
                    unlinkedData.map((data) => (
                      <DraggableText
                        key={data.id}
                        sourceId={data.id}
                        name={data.name}
                        moves={movesOf()}
                        onMove={handleDropItem}
                      />
                    ))
                  )}
                </DropBox>
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col className="d-flex justify-content-between">
            <ButtonToolbar>
              <SubmitButton
                className="me-2"
                disabled={isLoading || isSubmitting || unChanged}
                isSubmitting={isSubmitting}
                onClick={handleKeepChange}
              />
              <Button
                variant="danger"
                onClick={handleResetClick}
                disabled={isLoading || isSubmitting || unChanged}
              >
                Revert
              </Button>
            </ButtonToolbar>
            <ButtonToolbar>
              {additionalButtons}
              <SubmitButton
                onClick={handleAutoSuggest}
                disabled={isLoading || isSubmitting}
                isSubmitting={isLoadingSuggestions}
                label="Auto-Match"
                className="ms-2"
              />
            </ButtonToolbar>
          </Col>
        </Row>
      </Content>
    </>
  );
}

export default DataMappingModal;
